(() => {
  if (customElements.get('sg-card-emi')) return;

  const endpoint = 'https://receiver.cashfree.com/pgnextgenconsumer/uiapi/external/merchant/widget/offer';
  const cache = new Map();
  const pending = new Map();
  const queue = [];
  const elements = new Set();
  const lifetime = 5 * 60 * 1000;
  let activeRequests = 0;

  function parseOffer(data, amount) {
    const details = data && data.emiDetails;
    if (!details || !Number.isFinite(details.minEmiAmount) || details.minEmiAmount <= 0) return null;
    if (!Array.isArray(details.ccEMIPlans)) return null;
    const plans = [];
    for (const bank of details.ccEMIPlans) {
      if (!bank || typeof bank.name !== 'string' || !Array.isArray(bank.schemes)) continue;
      for (const plan of bank.schemes) {
        if (!plan || ![plan.emiAmount, plan.months, plan.totalAmount, plan.interest, plan.minAmount, plan.maxAmount].every(Number.isFinite)) continue;
        if (plan.emiAmount <= 0 || !Number.isInteger(plan.months) || plan.months < 1 || plan.months > 120) continue;
        if (plan.minAmount > amount || plan.maxAmount < amount || plan.totalAmount < amount || plan.interest < 0 || plan.interest > 100) continue;
        if (Math.abs(plan.emiAmount * plan.months - plan.totalAmount) > plan.months) continue;
        plans.push(plan);
      }
    }
    if (!plans.length) return null;
    const minimum = Math.min(...plans.map((plan) => plan.emiAmount));
    if (Math.abs(minimum - details.minEmiAmount) > 0.005) return null;
    const plan = plans.filter((item) => Math.abs(item.emiAmount - minimum) <= 0.005).sort((a, b) => a.months - b.months)[0];
    return { monthly: details.minEmiAmount, months: plan.months, interest: plan.interest };
  }

  function runQueue() {
    while (activeRequests < 2 && queue.length) {
      const job = queue.shift();
      activeRequests += 1;
      job().finally(() => {
        activeRequests -= 1;
        runQueue();
      });
    }
  }

  function getQuote(shop, cents) {
    const key = `${shop}:${cents}:INR`;
    const saved = cache.get(key);
    if (saved && saved.expires > Date.now()) return Promise.resolve(saved);
    if (pending.has(key)) return pending.get(key);
    const promise = new Promise((resolve) => {
      queue.push(async () => {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 8000);
        let quote = null;
        try {
          const response = await fetch(endpoint, {
            method: 'POST',
            mode: 'cors',
            credentials: 'omit',
            cache: 'no-store',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ shopId: shop, amount: (cents / 100).toFixed(2) }),
            signal: controller.signal,
          });
          if (response.ok) quote = parseOffer(await response.json(), cents / 100);
        } catch (_) {
          quote = null;
        } finally {
          clearTimeout(timeout);
        }
        const result = { quote, expires: Date.now() + (quote ? lifetime : 30000) };
        cache.set(key, result);
        pending.delete(key);
        resolve(result);
      });
    });
    pending.set(key, promise);
    runQueue();
    return promise;
  }

  const observer = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    for (const entry of entries) {
      for (const element of entry.target.querySelectorAll('sg-card-emi')) {
        element.nearViewport = entry.isIntersecting;
        if (entry.isIntersecting) element.refresh();
      }
    }
  }, { rootMargin: '200px 0px' }) : null;

  class CardEmi extends HTMLElement {
    static get observedAttributes() { return ['data-amount', 'data-currency', 'data-shop']; }

    connectedCallback() {
      elements.add(this);
      this.card = this.closest('.product-card-wrapper');
      this.generation = (this.generation || 0) + 1;
      this.hidden = true;
      this.nearViewport = !observer;
      if (observer && this.card) observer.observe(this.card);
      else this.refresh();
    }

    disconnectedCallback() {
      elements.delete(this);
      this.generation += 1;
      clearTimeout(this.expiryTimer);
      if (observer && this.card) observer.unobserve(this.card);
    }

    attributeChangedCallback() {
      this.generation = (this.generation || 0) + 1;
      this.hidden = true;
      clearTimeout(this.expiryTimer);
      if (this.isConnected && this.nearViewport) this.refresh();
    }

    async refresh() {
      if (!this.isConnected || !this.nearViewport || document.hidden) return;
      const cents = Number(this.dataset.amount);
      const shop = this.dataset.shop;
      const generation = this.generation;
      if (this.dataset.currency !== 'INR' || !Number.isSafeInteger(cents) || cents <= 0 || cents > 100000000000 || !/^[a-z0-9-]+\.myshopify\.com$/.test(shop || '')) {
        this.hidden = true;
        return;
      }
      const result = await getQuote(shop, cents);
      if (!this.isConnected || this.generation !== generation || document.hidden) return;
      clearTimeout(this.expiryTimer);
      if (!result.quote || result.expires <= Date.now()) {
        this.hidden = true;
        return;
      }
      const money = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(result.quote.monthly);
      this.querySelector('[data-emi-payment]').textContent = this.dataset.payment.replace('[amount]', money);
      this.querySelector('[data-emi-terms]').textContent = this.dataset.terms.replace('[months]', String(result.quote.months)).replace('[interest]', String(result.quote.interest));
      this.hidden = false;
      this.expiryTimer = setTimeout(() => {
        this.hidden = true;
        this.refresh();
      }, Math.max(0, result.expires - Date.now()));
    }
  }

  customElements.define('sg-card-emi', CardEmi);
  document.addEventListener('visibilitychange', () => {
    for (const element of elements) {
      if (document.hidden) element.hidden = true;
      else element.refresh();
    }
  });
})();
